import { attr, child, childElements, localName, text } from './dom.js';
import { Rational } from './rational.js';
import type { NoteEvent, TempoEvent } from './types.js';
const dur=(el:Element,div:Rational)=>Rational.parse(text(el,'duration')).div(div);
export function measureDurationQuarters(measure:Element,divIn:Rational):[Rational,Rational]{
 let div=divIn,c=Rational.zero,max=Rational.zero,last=Rational.zero;
 for(const el of childElements(measure)){
  const tag=localName(el);
  if(tag==='attributes'){const v=text(el,'divisions');if(v!=null)div=Rational.parse(v);}
  else if(tag==='backup')c=c.sub(dur(el,div));
  else if(tag==='forward'){c=c.add(dur(el,div));max=max.max(c);}
  else if(tag==='note'&&!child(el,'grace')){const d=dur(el,div);if(!child(el,'chord')){last=c;c=c.add(d);max=max.max(c);}else max=max.max(last.add(d));}
 }
 return[max,div];
}
export function partMeasureDurations(part:Element):Rational[]{let div=Rational.one;return childElements(part,'measure').map(m=>{const [d,n]=measureDurationQuarters(m,div);div=n;return d;});}
export function summarizePart(part:Element):Rational{return partMeasureDurations(part).reduce((a,b)=>a.add(b),Rational.zero);}

function soundOffset(sound:Element,parentDirection:Element|null,div:Rational):Rational{
 const own=child(sound,'offset');
 if(own)return Rational.parse(own.textContent).div(div);
 if(parentDirection){
  const off=child(parentDirection,'offset');
  if(off&&attr(off,'sound')==='yes')return Rational.parse(off.textContent).div(div);
 }
 return Rational.zero;
}

export function measureTempoEvents(measure:Element,divIn:Rational):[TempoEvent[],Rational,Rational]{
 let div=divIn,c=Rational.zero,max=Rational.zero,last=Rational.zero;const out:TempoEvent[]=[];
 const add=(sound:Element,parentDirection:Element|null)=>{
  const value=attr(sound,'tempo');if(value==null)return;
  const bpm=Rational.parse(value);if(bpm.eq(Rational.zero))throw new Error('MusicXML tempo=0 requires an interactive user tempo and cannot be converted deterministically.');if(bpm.cmp(Rational.zero)<0)throw new Error(`Invalid MusicXML tempo=${value}`);
  out.push({onset:c.add(soundOffset(sound,parentDirection,div)),bpm});
 };
 for(const el of childElements(measure)){
  const tag=localName(el);
  if(tag==='attributes'){const v=text(el,'divisions');if(v!=null)div=Rational.parse(v);continue;}
  if(tag==='sound'){add(el,null);continue;}
  if(tag==='direction'){for(const s of childElements(el,'sound'))add(s,el);continue;}
  if(tag==='backup'){c=c.sub(dur(el,div));continue;}
  if(tag==='forward'){c=c.add(dur(el,div));max=max.max(c);continue;}
  if(tag!=='note'||child(el,'grace'))continue;
  const d=dur(el,div);if(!child(el,'chord')){last=c;c=c.add(d);max=max.max(c);}else max=max.max(last.add(d));
 }
 return[out,div,max];
}

export function absoluteTempoEvents(part:Element):TempoEvent[]{
 let div=Rational.one,base=Rational.zero;const out:TempoEvent[]=[];
 for(const m of childElements(part,'measure')){
  const [events,n,extent]=measureTempoEvents(m,div);
  out.push(...events.map(e=>({onset:base.add(e.onset),bpm:e.bpm})));
  div=n;base=base.add(extent);
 }
 return out;
}

export function scoreTempoEvents(doc:Document):TempoEvent[]{
 const merged=new Map<string,TempoEvent>();
 for(const part of childElements(doc.documentElement,'part'))for(const e of absoluteTempoEvents(part)){
  const k=e.onset.toString(),old=merged.get(k);
  if(old&&!old.bpm.eq(e.bpm))throw new Error(`Conflicting tempos at quarter ${k}: ${old.bpm} vs ${e.bpm}`);
  merged.set(k,e);
 }
 const events=[...merged.values()].sort((a,b)=>a.onset.cmp(b.onset));
 if(!events.length||!events[0]!.onset.eq(Rational.zero))events.unshift({onset:Rational.zero,bpm:new Rational(120)});
 return events;
}

export function playbackSeconds(doc:Document,total?:Rational):number{
 const parts=childElements(doc.documentElement,'part');
 if(!parts.length)return 0;
 const end=total??summarizePart(parts[0]!);const events=scoreTempoEvents(doc);
 let cursor=Rational.zero,bpm=new Rational(120),seconds=0;
 for(const e of events){
  if(e.onset.cmp(end)>0)break;
  if(e.onset.cmp(cursor)>0){seconds+=Number(e.onset.sub(cursor).n)/Number(e.onset.sub(cursor).d)*60/(Number(bpm.n)/Number(bpm.d));cursor=e.onset;}
  bpm=e.bpm;
 }
 if(end.cmp(cursor)>0){const span=end.sub(cursor);seconds+=Number(span.n)/Number(span.d)*60/(Number(bpm.n)/Number(bpm.d));}
 return seconds;
}

export function tempoAtMeasureStarts(doc:Document):Rational[]{
 const first=childElements(doc.documentElement,'part')[0];if(!first)return[];
 const starts:Rational[]=[];let p=Rational.zero;for(const d of partMeasureDurations(first)){starts.push(p);p=p.add(d);}
 const events=scoreTempoEvents(doc);const out:Rational[]=[];let j=0,current=new Rational(120);
 for(const start of starts){while(j<events.length&&events[j]!.onset.cmp(start)<=0){current=events[j]!.bpm;j++;}out.push(current);}
 return out;
}
function pitchToMidi(note:Element):number|null{const p=child(note,'pitch');if(!p)return null;const step=text(p,'step'),oct=text(p,'octave');if(!step||oct==null)return null;const pc:Record<string,number>={C:0,D:2,E:4,F:5,G:7,A:9,B:11};const alter=Rational.parse(text(p,'alter'));if(alter.d!==1n)throw new Error(`Reference MIDI does not approximate microtonal alter=${alter}`);return 12*(Number(oct)+1)+pc[step]!+Number(alter.n);}
export function measureNoteEvents(measure:Element,divIn:Rational):[NoteEvent[],Rational,Rational]{
 let div=divIn,c=Rational.zero,max=Rational.zero,last=Rational.zero;const out:NoteEvent[]=[];
 for(const el of childElements(measure)){
  const tag=localName(el);
  if(tag==='attributes'){const v=text(el,'divisions');if(v!=null)div=Rational.parse(v);continue;}
  if(tag==='backup'){c=c.sub(dur(el,div));continue;} if(tag==='forward'){c=c.add(dur(el,div));max=max.max(c);continue;}
  if(tag!=='note'||child(el,'grace'))continue;const d=dur(el,div);let onset:Rational;if(!child(el,'chord')){onset=c;last=onset;c=c.add(d);max=max.max(c);}else{onset=last;max=max.max(onset.add(d));}
  const midi=pitchToMidi(el);if(midi==null)continue;const attack=Rational.parse(attr(el,'attack')).div(div),release=Rational.parse(attr(el,'release')).div(div);const dyn=Number(attr(el,'dynamics')??'100');if(dyn<=0)continue;const velocity=Math.max(1,Math.min(127,Math.round(90*dyn/100)));const ed=d.add(release).sub(attack);out.push({onset:onset.add(attack),duration:ed.cmp(Rational.zero)<0?Rational.zero:ed,midi,velocity});
 }
 return[out,div,max];
}
export function absoluteNoteEvents(part:Element):NoteEvent[]{let div=Rational.one,base=Rational.zero;const out:NoteEvent[]=[];for(const m of childElements(part,'measure')){const [events,n,extent]=measureNoteEvents(m,div);out.push(...events.map(e=>({...e,onset:base.add(e.onset)})));div=n;base=base.add(extent);}return out;}
